import { formatScientific } from '@/utils/formatters'
import { upperFirst, omit, isNil, isString, isNumber, isEmpty, get } from 'lodash-es'
import { mapUnitOrOriginal } from '@/components/combined-predict/components/CombinedPredictTable/utils/unitConversions'

const idMapping = {
  totalCoefficient: { name: 'Contribution' },
  maxFragmentCount: { name: 'Max Frag' },
  validationCount: { name: 'Validation' },
  maxValidationFragmentCount: { name: 'Max Val Frag' },
  dermalPermeabilityCoefficient: { name: 'K<sub>p</sub>', unit: 'cm/hr' },
  lagTimePerEventHours: { name: 'τ (Lag Time)' },
  timeToReachSteadyStateHours: { name: 'τ* (Steady State)' },
  MassPerHour: { name: 'g/hour' },
  MolPerHour: { name: 'mol/hour' },
  Percent: { name: '%' },
  maxLogKow: { name: 'Max Log K<sub>ow</sub>' },
  mmHg: { name: 'mmHg' },
  pa: { name: 'Pa' },
  dermalAbsorbedDosePerEvent: { unit: 'mg/cm²-event' },
  dermalAbsorbedDose: { unit: 'mg/kg-day' },
  hlcAtm: { name: "Henry's Law Constant", unit: 'atm-m³/mol' },
  hlcPaMol: { name: "Henry's Law Constant", unit: 'Pa-m³/mol' },
  hlcUnitless: { name: "Henry's Law Constant", unit: 'Unitless' },
  riverWaterDepthMeters: { name: 'Water Depth', unit: 'meters' },
  riverWindVelocityMetersPerSecond: { name: 'Wind Velocity', unit: 'meters/second' },
  riverCurrentVelocityMetersPerSecond: { name: 'Current Velocity', unit: 'meters/second' },
  lakeWindVelocityMetersPerSecond: { name: 'Wind Velocity', unit: 'meters/second' },
  lakeCurrentVelocityMetersPerSecond: { name: 'Current Velocity', unit: 'meters/second' },
  lakeWaterDepthMeters: { name: 'Water Depth', unit: 'meters' },
}

const idToTitle = (id) => {
  if (get(idMapping, id)?.name) return get(idMapping, id).name
  // if (isString(id)) {
  //   if (id.includes('perHour')) return id.replace('perHour', '/hour')
  // }

  return id.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase())
}

const getNonNilArray = (array) => (Array.isArray(array) ? array.filter((item) => !isNil(item)) : [])

const getReference = (item) => {
  if (item?.year) return `${item?.author ?? ''} (${item.year})`
  return `${item?.author ?? ''}`
}

const toSentenceCase = (text) => {
  if (isNil(text)) return null
  if (typeof text !== 'string') return text
  return upperFirst(text.toLowerCase())
}

const formatModelValue = (value) => formatScientific(value, { blank: '-' })
const formatModelUnit = (unit) => mapUnitOrOriginal(unit)

const processPredictionData = (data) => ({
  ...omit(data, ['author', 'year']),
  valueType: toSentenceCase(data?.valueType),
  value: formatModelValue(data?.value),
  unit: formatModelUnit(data?.units ?? data?.unit),
  reference: data?.author ? getReference(data) : null,
})

const getSummaryValue = (selectedData, title) => {
  if (isNil(selectedData?.value)) return { value: 'N/A' }

  const processedData = processPredictionData(selectedData)
  const cleanedTitle = title || ''
  return {
    ...selectedData,
    title: processedData?.valueType ? `${processedData.valueType} ${cleanedTitle}` : cleanedTitle,
    value: processedData?.value,
    unit: processedData?.unit,
  }
}

const mapProcessedDataToDisplayTextBuilder = (processedData) => ({
  ...processedData,
  mainText: processedData?.value,
  secondaryText: processedData?.unit,
  belowText: processedData?.belowText,
})

const buildDisplayText = (value) => {
  const {
    mainText = null,
    belowText = null,
    secondaryText = null,
    mainTextClass = null,
    flags = null,
  } = isNil(value) ? {} : value
  return !isNil(mainText)
    ? { mainText, belowText: toSentenceCase(belowText), secondaryText, mainTextClass, flags }
    : typeof value === 'number' || typeof value === 'string'
      ? value
      : null
}

const makeExpEstRow = (experimentalValues, predictedValue, titleData) => {
  return [
    titleData,
    processPredictionData(predictedValue)?.value,
    experimentalValues?.length ? experimentalValues.map((expVal) => processPredictionData(expVal)?.value) : '-',
    experimentalValues?.length ? experimentalValues.map((expVal) => processPredictionData(expVal)?.reference) : '-',
  ]
}

const makeExpEstHeaders = ({
  estimated = 'Estimated',
  experimental = 'Experimental',
  references = 'References',
  extra = [],
} = {}) => ['', estimated, experimental, references, ...extra].filter((header) => header !== null)

const toDisplayObject = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : { value })

const buildDisplayNameCell = (displayName, displayValue) => {
  const displayNameObj = toDisplayObject(displayName)

  const displayNamePayload = {
    ...displayNameObj,
    value: idToTitle(displayNameObj?.value ?? displayNameObj),
    belowText: displayNameObj?.belowText ?? displayValue?.valueType,
  }
  const processedDisplayName = processPredictionData(displayNamePayload)
  const mappedDisplayName = mapProcessedDataToDisplayTextBuilder(processedDisplayName)
  return buildDisplayText(mappedDisplayName)
}

const getUnitFromDisplayName = (displayName) => {
  if (get(idMapping, displayName)?.unit) return get(idMapping, displayName).unit

  if (isString(displayName)) {
    if (displayName.endsWith('Hours')) return mapUnitOrOriginal('hours')
  }
}

const buildDisplayValueCell = (displayValue, displayName) => {
  // console.log('displayValue in buildDisplayValueCell:', displayName, getUnitFromDisplayName(displayName))
  const displayValueObj = toDisplayObject(displayValue)

  const unit = displayValueObj?.unit ?? getUnitFromDisplayName(displayName)

  const displayValueWithUnit = {
    ...displayValueObj,
    ...(unit ? { unit } : {}),
  }

  const processedDisplayValue = mapProcessedDataToDisplayTextBuilder(
    processPredictionData(omit(displayValueWithUnit, 'valueType')),
  )
  return buildDisplayText(processedDisplayValue)
}

const buildDisplayPairRow = (displayName, displayValue) => {
  return [
    buildDisplayNameCell(displayName, displayValue),
    Array.isArray(displayValue)
      ? displayValue.map(buildDisplayValueCell)
      : buildDisplayValueCell(displayValue, displayName),
  ]
}

const makeFactorRow = (factor) => {
  return Object.entries(factor).map(([key, val]) => {
    if (
      key !== 'trainingCount' &&
      key !== 'fragmentCount' &&
      key !== 'validationCount' &&
      key !== 'maxFragmentCount' &&
      key !== 'maxValidationFragmentCount'
    ) {
      if (key === 'units') return mapUnitOrOriginal(val)
      if (val?.value) return buildDisplayValueCell(val)
      return typeof val === 'number' ? formatModelValue(val) : val
    }

    return val.toString()
  })
}

const makeFactorRows = (factors) => (!factors?.length ? [] : factors.map(makeFactorRow))

const makeFactorHeaders = (factors) => (!factors?.length ? [] : Object.keys(factors[0]).map(idToTitle))

const makeFactorsLikeArrayObject = (parameters) =>
  Object.entries(parameters).map(([key, value]) => ({
    type: idToTitle(key),
    ...value,
    ...(value?.valueType ? { valueType: toSentenceCase(value?.valueType) } : {}),
  }))

const makeFactorTable = (factors, title) =>
  !factors?.length
    ? null
    : {
        title,
        headers: makeFactorHeaders(factors),
        rows: makeFactorRows(factors),
      }

const makeFooter = (data) =>
  Object.entries(data ?? {})
    .map(([key, displayValue]) => {
      if (key === 'name') return null
      if (!isString(displayValue) && !isNumber(displayValue) && !displayValue?.value) return null
      if (isString(displayValue) && displayValue.trim() === '') return null
      return buildDisplayPairRow(key, displayValue)
    })
    .filter(Boolean)

const makeModelTable = (model, name) => {
  const headers = makeFactorHeaders(model?.factors)
  const rows = makeFactorRows(model?.factors)
  const footer = makeFooter(model)

  if (!headers.length && !rows.length && !footer.length) return null

  return {
    title: name ?? model?.name ?? null,
    headers,
    rows,
    footer,
  }
}

const makeModelTables = (models) =>
  !models?.length ? [] : models.map((model) => makeModelTable(model)).filter(Boolean)

const makeParameterTable = (parameters) => {
  const mainParameters = omit(parameters, ['smiles', 'cas', 'databasePath'])
  const factorsLikeArray = makeFactorsLikeArrayObject(mainParameters)
  return !isEmpty(factorsLikeArray) ? makeFactorTable(factorsLikeArray, 'Parameters') : null
}

const getTitle = (meta) => meta?.shortTitle ?? meta?.name

export {
  formatModelValue,
  getSummaryValue,
  getNonNilArray,
  makeExpEstRow,
  makeExpEstHeaders,
  makeFactorRows,
  makeFactorsLikeArrayObject,
  makeParameterTable,
  makeFactorTable,
  makeFooter,
  makeModelTable,
  makeModelTables,
  makeFactorHeaders,
  getTitle,
  idToTitle,
  buildDisplayText,
  processPredictionData,
  buildDisplayPairRow,
}
